<script>
import WhitelabelFileTypes from '@/enums/WhitelabelFileTypes'
import defaultPrimaryFont from '@/data/defaults/primary.font'
import defaultSecondaryFont from '@/data/defaults/secondary.font'

export default {
  data() {
    return {
      maxUploadSize: 1024 * 1024,
      allowedFileTypes: '.woff,.woff2,.ttf,.otf,.svg, .eot',
      primaryFontFiles: null,
      secondaryFontFiles: null,
      isEditingModePrimary: false,
      isEditingModeSecondary: false,
      fontWeightOptions: [
        { text: '100 (Thin)', value: '100' },
        { text: '200 (Extra Light)', value: '200' },
        { text: '300 (Light)', value: '300' },
        { text: '400 (Normal)', value: '400' },
        { text: '500 (Medium)', value: '500' },
        { text: '600 (Semi Bold)', value: '600' },
        { text: '700 (Bold)', value: '700' },
        { text: '800 (Extra Bold)', value: '800' },
        { text: '900 (Black)', value: '900' }
      ],
      fontStyleOptions: [
        { text: 'Normal', value: 'normal' },
        { text: 'Italic', value: 'italic' },
        { text: 'Oblique', value: 'oblique' },
        { text: 'Initial', value: 'initial' },
        { text: 'Inherit', value: 'inherit' }
      ]
    }
  },
  computed: {
    WhitelabelFileTypes() {
      return WhitelabelFileTypes
    },
    primaryFont: {
      get() {
        const font = this.$store.state?.whitelabel?.fonts?.primary || {};
        return {
          name: font.name || defaultPrimaryFont.name,
          weight: font.weight || defaultPrimaryFont.weight,
          style: font.style || defaultPrimaryFont.style,
          files: font.files || [],
          fileNames: font.fileNames || []
        };
      },
      set(value) {
        if (this.$store.state?.whitelabel?.fonts) {
          this.$store.state.whitelabel.fonts.primary = value
        }
      }
    },
    secondaryFont: {
      get() {
        const font = this.$store.state?.whitelabel?.fonts?.secondary || {};
        return {
          name: font.name || defaultSecondaryFont.name,
          weight: font.weight || defaultSecondaryFont.weight,
          style: font.style || defaultSecondaryFont.style,
          files: font.files || [],
          fileNames: font.fileNames || []
        };
      },
      set(value) {
        if (this.$store.state?.whitelabel?.fonts) {
          this.$store.state.whitelabel.fonts.secondary = value
        }
      }
    },
    isPrimaryFontValid () {
      const font = this.primaryFont;
      return (
        font.name?.trim().length >= 3 &&
        !!font.weight &&
        !!font.style &&
        Array.isArray(font.files) &&
        font.files.length > 0
      );
    },
    isSecondaryFontValid () {
      const font = this.secondaryFont;
      return (
        font.name?.trim().length >= 3 &&
        !!font.weight &&
        !!font.style &&
        Array.isArray(font.files) &&
        font.files.length > 0
      );
    }
  },
  methods: {
    async submitFont(type) {
      const formData = new FormData()
      formData.append('Type', type)

      const font =
        type === WhitelabelFileTypes.PrimaryFont
          ? this.primaryFont
          : this.secondaryFont

      formData.append('Name', font.name)
      formData.append('Weight', font.weight)
      formData.append('Style', font.style)

      font.files.forEach(file => {
        formData.append('Files', file)
      })
      this.isEditingModePrimary = false
      this.isEditingModeSecondary = false
      await this.$store.dispatch('putFont', {font: formData, type})
    },

    resetTypography() {
      this.isEditingModePrimary = false
      this.isEditingModeSecondary = false
      this.$store.dispatch('resetFonts')
    }
  }
}
</script>

<template>
  <v-container :key="isEditingModePrimary" fluid>
    <!--    HEADER-->
    <v-row>
      <v-col cols="12">
        <h3 class="float-left">{{$t('component.whitelabel.custom.typography.heading')}}</h3>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn
              id="reset-default-typography"
              class="float-right"
              icon
              color="primary"
              v-on="on"
              @click="resetTypography">
              <v-icon>mdi-autorenew</v-icon>
            </v-btn>
          </template>
          <span>{{$t('component.whitelabel.custom.typography.reset.button.tooltip')}}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-divider></v-divider>

    <!--    PRIMARY FONT-->
    <v-row id="custom-primary-font-container">
      <v-col cols="12" md="6">
        <h4 class="float-left">
          {{ $t('component.whitelabel.custom.typography.primary.heading') }}
        </h4>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span>{{$t('component.whitelabel.custom.typography.primary.description.tooltip')}}</span>
        </v-tooltip>
        <v-card outlined class="pa-4 my-4">
          <v-text-field
            id="custom-primary-font-name"
            v-model="primaryFont.name"
            outlined
            clearable
            :disabled="!isEditingModePrimary"
            :label="$t('component.whitelabel.custom.typography.font.name')" />
          <v-select
            id="custom-primary-font-weight"
            v-model="primaryFont.weight"
            :label="$t('component.whitelabel.custom.typography.font.weight')"
            :items="fontWeightOptions"
            :disabled="!isEditingModePrimary"
            item-text="text"
            item-value="value"
            dense
            outlined />
          <v-select
            id="custom-primary-font-style"
            v-model="primaryFont.style"
            :label="$t('component.whitelabel.custom.typography.font.style')"
            :items="fontStyleOptions"
            :disabled="!isEditingModePrimary"
            item-text="text"
            item-value="value"
            dense
            outlined />
          <div v-if="primaryFont.fileNames && primaryFont.fileNames.length > 0 && !isEditingModePrimary">
            <span class="text-caption">
              {{ primaryFont.fileNames.map(file => file).join(', ') }}
            </span>
          </div>
          <v-file-input
            v-else
            id="custom-primary-font-file-input"
            v-model="primaryFont.files"
            :disabled="!isEditingModePrimary"
            label="Dateien hochladen"
            :accept="allowedFileTypes"
            multiple
            show-size
            prepend-icon="mdi-upload" />
        </v-card>
        <!-- SAVE BUTTON -->
        <v-row v-if="$store.getters.urls.subdomain && isEditingModePrimary" >
          <v-col>
            <v-btn
              id="cancel-primary-font-button"
              color="secondary"
              block
              @click="isEditingModePrimary = false">
              <v-icon class="pe-3">mdi-x</v-icon>
              {{ $t('button.cancel') }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              id="save-primary-font-button"
              color="primary"
              block
              :disabled="isPrimaryFontValid"
              @click="submitFont(WhitelabelFileTypes.PrimaryFont)">
              <v-icon class="pe-3">mdi-check</v-icon>
              {{ $t('button.save') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="!isEditingModePrimary">
          <v-col cols="12">
            <v-btn
              id="edit-primary-font-button"
              color="primary"
              block
              @click="isEditingModePrimary = true">
              <v-icon class="pe-3">mdi-pencil</v-icon>
              {{ $t('button.edit') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>

      <!--    SECONDARY FONT-->
      <v-col id="custom-secondary-font-container" cols="12" md="6">
        <h4 class="float-left">
          {{ $t('component.whitelabel.custom.typography.secondary.heading') }}
        </h4>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span>{{$t('component.whitelabel.custom.typography.secondary.description.tooltip')}}</span>
        </v-tooltip>
        <v-card outlined class="pa-4 my-4">
          <v-text-field
            id="custom-secondary-font-name"
            v-model="secondaryFont.name"
            outlined
            clearable
            :disabled="!isEditingModeSecondary"
            :label="$t('component.whitelabel.custom.typography.font.name')" />
          <v-select
            id="custom-secondary-font-weight"
            v-model="secondaryFont.weight"
            :label="$t('component.whitelabel.custom.typography.font.weight')"
            :items="fontWeightOptions"
            :disabled="!isEditingModeSecondary"
            item-text="text"
            item-value="value"
            dense
            outlined />
          <v-select
            id="custom-secondary-font-style"
            v-model="secondaryFont.style"
            :label="$t('component.whitelabel.custom.typography.font.style')"
            :items="fontStyleOptions"
            :disabled="!isEditingModeSecondary"
            item-text="text"
            item-value="value"
            dense
            outlined />
          <div v-if="secondaryFont.fileNames && secondaryFont.fileNames.length > 0 && !isEditingModeSecondary">
            <span class="text-caption">
              {{ secondaryFont.fileNames.map(file => file).join(', ') }}
            </span>
          </div>
          <v-file-input
            v-else
            id="custom-secondary-font-file-input"
            v-model="secondaryFont.files"
            label="Dateien hochladen"
            :disabled="!isEditingModeSecondary"
            :accept="allowedFileTypes"
            multiple
            show-size
            prepend-icon="mdi-upload" />
        </v-card>
        <!-- SAVE BUTTON -->
        <v-row v-if="$store.getters.urls.subdomain && isEditingModeSecondary" >
          <v-col>
            <v-btn
              id="cancel-secondary-font-button"
              color="secondary"
              block
              @click="isEditingModeSecondary = false">
              <v-icon class="pe-3">mdi-x</v-icon>
              {{ $t('button.cancel') }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              id="save-secondary-font-button"
              color="primary"
              block
              :disabled="isSecondaryFontValid"
              @click="submitFont(WhitelabelFileTypes.SecondaryFont)">
              <v-icon class="pe-3">mdi-check</v-icon>
              {{ $t('button.save') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="!isEditingModeSecondary">
          <v-col cols="12">
            <v-btn
              id="edit-secondary-font-button"
              color="primary"
              block
              @click="isEditingModeSecondary = true">
              <v-icon class="pe-3">mdi-pencil</v-icon>
              {{ $t('button.edit') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.text-caption {
  color: #666;
}
</style>
